// services/cart.service.js (Frontend)
import { http, URL } from './http.service';

// Get session ID from localStorage or create new one
const getSessionId = () => {
  let sessionId = localStorage.getItem('guest_session_id');
  if (!sessionId) {
    sessionId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('guest_session_id', sessionId);
  }
  return sessionId;
};

// Helper function to normalize cart response
const normalizeCartResponse = (response) => {
  // If response is already in { success, data } format
  if (response && response.success) {
    // Check if data has items array
    if (response.data && response.data.items) {
      return {
        success: true,
        data: {
          items: response.data.items
        }
      };
    }
    // If data is directly the cart object with items
    else if (response.data && response.data.cart && response.data.cart.items) {
      return {
        success: true,
        data: {
          items: response.data.cart.items
        }
      };
    }
    // If data is an array (direct items array)
    else if (Array.isArray(response.data)) {
      return {
        success: true,
        data: {
          items: response.data
        }
      };
    }
    // If data is a cart object with items at top level
    else if (response.data && response.data.items) {
      return {
        success: true,
        data: {
          items: response.data.items
        }
      };
    }
  }
  
  // If response is directly the data (from http interceptor)
  if (response && response.data && response.data.success) {
    if (response.data.data && response.data.data.items) {
      return {
        success: true,
        data: {
          items: response.data.data.items
        }
      };
    } else if (response.data.data && response.data.data.cart && response.data.data.cart.items) {
      return {
        success: true,
        data: {
          items: response.data.data.cart.items
        }
      };
    } else if (Array.isArray(response.data.data)) {
      return {
        success: true,
        data: {
          items: response.data.data
        }
      };
    }
  }
  
  // Default fallback
  return {
    success: false,
    data: { items: [] }
  };
};

// Get cart for logged-in user
export const getUserCart = async (businessUuid, customerId) => {
  try {
    console.log('Fetching cart for user:', customerId);
    const response = await http.get(`${URL}/cart/${businessUuid}/user/${customerId}`);
    console.log('Raw getUserCart response:', response);
    
    // Normalize the response
    const normalized = normalizeCartResponse(response);
    console.log('Normalized cart response:', normalized);
    
    return normalized;
  } catch (error) {
    console.error('Error fetching user cart:', error);
    return { 
      success: false, 
      data: { items: [] },
      error: error.message 
    };
  }
};

// Get cart for guest user
export const getGuestCart = async (businessUuid) => {
  try {
    const sessionId = getSessionId();
    const response = await http.get(`${URL}/cart/${businessUuid}/guest/${sessionId}`);
    return normalizeCartResponse(response);
  } catch (error) {
    console.error('Error fetching guest cart:', error);
    return { success: false, data: { items: [] } };
  }
};

// Add to cart for logged-in user
export const addToUserCart = async (businessUuid, customerId, itemData) => {
  try {
    console.log('Adding to cart for user:', customerId, 'item:', itemData);
    const response = await http.post(`${URL}/cart/${businessUuid}/user/${customerId}/add`, itemData);
    console.log('Add to cart response:', response);
    
    // Check if the response indicates success
    if (response && response.data && response.data.success) {
      return {
        success: true,
        data: response.data.data,
        message: 'Item added to cart successfully'
      };
    } else if (response && response.success) {
      return {
        success: true,
        data: response.data,
        message: 'Item added to cart successfully'
      };
    }
    
    return {
      success: false,
      message: 'Failed to add item to cart'
    };
  } catch (error) {
    console.error('Error adding to user cart:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to add to cart',
      error
    };
  }
};

// Add to cart for guest user
export const addToGuestCart = async (businessUuid, itemData) => {
  try {
    const sessionId = getSessionId();
    const response = await http.post(`${URL}/cart/${businessUuid}/guest/${sessionId}/add`, itemData);
    
    if (response && response.data && response.data.success) {
      return {
        success: true,
        data: response.data.data,
        message: 'Item added to cart successfully'
      };
    }
    
    return { success: false, message: 'Failed to add to cart' };
  } catch (error) {
    console.error('Error adding to guest cart:', error);
    return { success: false, message: error.message };
  }
};

// Buy now for logged-in user
export const buyNowUser = async (businessUuid, customerId, itemData) => {
  try {
    console.log('Buy now for user:', customerId, 'item:', itemData);
    const response = await http.post(`${URL}/cart/${businessUuid}/user/${customerId}/buy-now`, itemData);
    console.log('Buy now response:', response);
    
    // Check different response structures
    if (response && response.data && response.data.success) {
      return {
        success: true,
        data: response.data.data,
        message: 'Item added to cart successfully'
      };
    } else if (response && response.success) {
      return {
        success: true,
        data: response.data,
        message: 'Item added to cart successfully'
      };
    }
    
    return {
      success: false,
      message: 'Failed to add item to cart'
    };
  } catch (error) {
    console.error('Error in buy now for user:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to add to cart',
      error
    };
  }
};

// Buy now for guest user
export const buyNowGuest = async (businessUuid, itemData) => {
  try {
    const sessionId = getSessionId();
    const response = await http.post(`${URL}/cart/${businessUuid}/guest/${sessionId}/buy-now`, itemData);
    
    if (response && response.data && response.data.success) {
      return {
        success: true,
        data: response.data.data,
        message: 'Item added to cart successfully'
      };
    }
    
    return { success: false, message: 'Failed to add to cart' };
  } catch (error) {
    console.error('Error in buy now for guest:', error);
    return { success: false, message: error.message };
  }
};

// Update cart item quantity for logged-in user
export const updateUserCartQuantity = async (businessUuid, customerId, productId, quantity, variantKey = null) => {
  try {
    const response = await http.put(`${URL}/cart/${businessUuid}/user/${customerId}/update`, {
      productId,
      quantity,
      variantKey
    });
    
    if (response && response.data && response.data.success) {
      return {
        success: true,
        data: response.data.data,
        message: 'Cart updated successfully'
      };
    }
    
    return { success: false, message: 'Failed to update cart' };
  } catch (error) {
    console.error('Error updating user cart:', error);
    return { success: false, message: error.message };
  }
};

// Update cart item quantity for guest user
export const updateGuestCartQuantity = async (businessUuid, productId, quantity, variantKey = null) => {
  try {
    const sessionId = getSessionId();
    const response = await http.put(`${URL}/cart/${businessUuid}/guest/${sessionId}/update`, {
      productId,
      quantity,
      variantKey
    });
    
    if (response && response.data && response.data.success) {
      return {
        success: true,
        data: response.data.data,
        message: 'Cart updated successfully'
      };
    }
    
    return { success: false, message: 'Failed to update cart' };
  } catch (error) {
    console.error('Error updating guest cart:', error);
    return { success: false, message: error.message };
  }
};

// Remove item from user cart
export const removeFromUserCart = async (businessUuid, customerId, productId, variantKey = null) => {
  try {
    const response = await http.delete(`${URL}/cart/${businessUuid}/user/${customerId}/remove`, {
      data: { productId, variantKey }
    });
    
    if (response && response.data && response.data.success) {
      return {
        success: true,
        message: 'Item removed from cart'
      };
    }
    
    return { success: false, message: 'Failed to remove item' };
  } catch (error) {
    console.error('Error removing from user cart:', error);
    return { success: false, message: error.message };
  }
};

// Remove item from guest cart
export const removeFromGuestCart = async (businessUuid, productId, variantKey = null) => {
  try {
    const sessionId = getSessionId();
    const response = await http.delete(`${URL}/cart/${businessUuid}/guest/${sessionId}/remove`, {
      data: { productId, variantKey }
    });
    
    if (response && response.data && response.data.success) {
      return {
        success: true,
        message: 'Item removed from cart'
      };
    }
    
    return { success: false, message: 'Failed to remove item' };
  } catch (error) {
    console.error('Error removing from guest cart:', error);
    return { success: false, message: error.message };
  }
};

// Clear user cart
export const clearUserCart = async (businessUuid, customerId) => {
  try {
    const response = await http.delete(`${URL}/cart/${businessUuid}/user/${customerId}/clear`);
    
    if (response && response.data && response.data.success) {
      return {
        success: true,
        message: 'Cart cleared successfully'
      };
    }
    
    return { success: false, message: 'Failed to clear cart' };
  } catch (error) {
    console.error('Error clearing user cart:', error);
    return { success: false, message: error.message };
  }
};

// Clear guest cart
export const clearGuestCart = async (businessUuid) => {
  try {
    const sessionId = getSessionId();
    const response = await http.delete(`${URL}/cart/${businessUuid}/guest/${sessionId}/clear`);
    
    if (response && response.data && response.data.success) {
      return {
        success: true,
        message: 'Cart cleared successfully'
      };
    }
    
    return { success: false, message: 'Failed to clear cart' };
  } catch (error) {
    console.error('Error clearing guest cart:', error);
    return { success: false, message: error.message };
  }
};

// Merge guest cart with user cart after login
export const mergeCarts = async (businessUuid, customerId) => {
  try {
    const sessionId = getSessionId();
    const response = await http.post(`${URL}/cart/${businessUuid}/merge`, {
      sessionId,
      customerId
    });
    
    // Clear guest session after merge
    localStorage.removeItem('guest_session_id');
    
    if (response && response.data && response.data.success) {
      return {
        success: true,
        data: response.data.data,
        message: 'Carts merged successfully'
      };
    }
    
    return { success: false, message: 'Failed to merge carts' };
  } catch (error) {
    console.error('Error merging carts:', error);
    return { success: false, message: error.message };
  }
};